import { scrubUrl } from './link-token';

/**
 * What the session recorders may not keep.
 *
 * LogRocket records every XHR and fetch body by default, and Sentry Replay does the same for
 * any host in `networkDetailAllowUrls`, which names ours. Between them, every credential the
 * API hands out or takes in would be exported to a third party's replay storage, usable as it
 * stands: the authenticator seed and the recovery codes, the session token, a password on its
 * way to the reset endpoint, a personal access token, a service-account token or a webhook
 * signing secret on its way back. The `sentry-mask` classes on the enrollment screen hide what
 * is *rendered*; they say nothing about the payload the screen was rendered from.
 *
 * One list, so the two recorders cannot drift apart. Paths, not hosts: the API base differs by
 * environment and the recorder sees absolute URLs.
 */

/**
 * API paths whose *response* bodies hand a credential back. Each is matched at the start of a
 * segment after `/api`, so `/two-factor-settings` would not match and `/two-factor/enroll`
 * does; a pattern may name a sub-path where only that one answers with a secret.
 */
const RESPONSE_PATHS = [
	// The session token; a sign-in link's and an invitation's answers.
	'session',
	// The authenticator seed (`enroll`) and the recovery codes (`confirm`, `recovery`).
	'two-factor',
	// WebAuthn challenges and the credentials that answer them.
	'passkeys',
	// A completed reset answers with a session.
	'password-reset',
	// Legacy token issuance.
	'tokens',
	// The raw token, returned exactly once on creation.
	'personal-access-tokens',
	// A service account's token, returned exactly once on issuance.
	'service-accounts/[^/?#]+/tokens',
	// The signing secret, on creation and on `roll-secret`.
	'webhook-endpoints',
	// An emailed one-shot: the token is in the path.
	'approval-actions'
];

/**
 * API paths whose *request* bodies carry a credential: everything above, since a request to any
 * of them carries a password, a code, a token or an assertion, plus `/users` for the
 * registration and password-change writes. `/users` responses are not redacted: they are the
 * profile data a replay is usually being read to see.
 */
const REQUEST_PATHS = [...RESPONSE_PATHS, 'users'];

const pathPattern = (paths) => new RegExp(`/api/(?:${paths.join('|')})(?:/|\\?|#|$)`);

export const SECRET_REQUEST_PATH = pathPattern(REQUEST_PATHS);

export const SECRET_RESPONSE_PATH = pathPattern(RESPONSE_PATHS);

/**
 * Sentry Replay's `networkDetailDenyUrls` takes strings matched by substring or regular
 * expressions matched against the full URL, and one list governs both directions -- so it is
 * the union, which is the request set. Regular expressions, so a query string cannot get a URL
 * past the list.
 */
export const SENTRY_NETWORK_DENY_URLS = [SECRET_REQUEST_PATH];

/**
 * Request headers that are a credential in themselves, whatever the path.
 */
const SECRET_HEADERS = ['authorization', 'cookie', 'set-cookie'];

export const REDACTED = '[redacted]';

/**
 * Whether a request to `url` may keep its body in a recording.
 *
 * @param {string} url Absolute or path-only.
 * @returns {boolean}
 */
export const requestCarriesSecret = (url) => SECRET_REQUEST_PATH.test(String(url || ''));

/**
 * Whether the response to a request to `url` may keep its body in a recording.
 *
 * @param {string} url Absolute or path-only.
 * @returns {boolean}
 */
export const responseCarriesSecret = (url) => SECRET_RESPONSE_PATH.test(String(url || ''));

const stripHeaders = (headers) => {
	if(!headers || typeof headers !== 'object') {
		return headers;
	}

	const cleaned = { ...headers };

	Object.keys(cleaned).forEach((name) => {
		if(SECRET_HEADERS.includes(name.toLowerCase())) {
			cleaned[name] = REDACTED;
		}
	});

	return cleaned;
};

/**
 * LogRocket's network sanitizers. Its response object carries no URL, only the `reqId` it shares
 * with the request, so the request sanitizer remembers which ids were secret and the response
 * sanitizer looks them up. The set is drained as responses arrive; a request that never gets a
 * response leaves one string behind, which is nothing.
 *
 * Returning `null` from a sanitizer drops the entry from the recording entirely. The entry is
 * kept here with its body redacted instead: that a call to `/two-factor/confirm` answered 422
 * is exactly what somebody debugging a replay needs, and the six digits are not.
 *
 * @returns {{ requestSanitizer: Function, responseSanitizer: Function }}
 */
export const createLogRocketSanitizers = () => {
	const secretRequests = new Set();

	return {
		requestSanitizer: (request) => {
			if(!request) {
				return request;
			}

			// The URL as well as the body: `/approval-actions/{token}` carries its credential in
			// the path. See `link-token.js`.
			const sanitized = {
				...request,
				url: scrubUrl(request.url),
				headers: stripHeaders(request.headers)
			};

			if(requestCarriesSecret(request.url)) {
				sanitized.body = REDACTED;
			}

			if(responseCarriesSecret(request.url)) {
				secretRequests.add(request.reqId);
			}

			return sanitized;
		},

		responseSanitizer: (response) => {
			if(!response) {
				return response;
			}

			const sanitized = { ...response, headers: stripHeaders(response.headers) };

			if(secretRequests.delete(response.reqId)) {
				sanitized.body = REDACTED;
			}

			return sanitized;
		}
	};
};

/**
 * LogRocket's DOM options. `data-private` is honoured by LogRocket out of the box; it is named
 * here so the list is explicit, and `sentry-block`/`sentry-mask` are added so the one class that
 * already marks a credential on screen hides it from both recorders.
 */
export const LOG_ROCKET_DOM_OPTIONS = {
	privateAttributeBlocklist: ['data-private'],
	privateClassNameBlocklist: ['sentry-block', 'sentry-mask']
};
